import {
  CHINA_CODE,
  COLOR_BORDER,
  COLOR_DEFAULT,
  COLOR_MAX,
  COLORS_LEGEND,
  COUNTRY_NAME,
  GREENLAND_CODE,
  HONG_KONG_CODE,
  MACAU_CODE,
  MAP_CONFIG,
  REGION_TO_COUNTRY_MAPPING,
  RUSSIA_CODE,
  SPECIAL_TERRITORY_MAPPING,
  TAIWAN_CODE,
} from './constants';

// ============ Работа со странами ============

export const isSpecialTerritoryCode = (code) => [TAIWAN_CODE, HONG_KONG_CODE, MACAU_CODE].includes(code);

export const getCountryCode = (selectedId, countries) => {
  const numericId = Number(selectedId);
  return countries.find((c) => c.id === numericId)?.code ?? null;
};

export const findCountryByCode = (code, countries) => {
  const numericCode = Number(code);
  return countries.find((c) => c.code === numericCode);
};

export const getCountryName = (countryData, feature) =>
  countryData?.nameFull || countryData?.name || feature?.properties?.name;

export const getRegionName = (item) => item.regionOfRussia?.name || item.regionOfChina?.name || item.name;

export const isRussianRegion = (regionData) => !!regionData?.regionOfRussia;

export const isChinaRegion = (regionData) => !!regionData?.regionOfChina;

export const getRegionalMapConfig = (regionType, countryCode) => {
  if (regionType === COUNTRY_NAME.RUSSIA) return MAP_CONFIG.RUSSIA;

  const configMap = {
    [TAIWAN_CODE]: MAP_CONFIG.TAIWAN,
    [HONG_KONG_CODE]: MAP_CONFIG.HONG_KONG,
    [MACAU_CODE]: MAP_CONFIG.MACAU,
  };

  return configMap[countryCode] || MAP_CONFIG.CHINA;
};

export const getRegionTypeFromRegionData = (regionData) => {
  if (isRussianRegion(regionData)) return COUNTRY_NAME.RUSSIA;
  if (isChinaRegion(regionData)) return COUNTRY_NAME.CHINA;
  return null;
};

export const getSelectedRegionCodesFromRegionData = (regionData) => {
  if (isRussianRegion(regionData)) {
    return regionData.regionOfRussia.regionCodes?.map(String) ?? [];
  }

  if (isChinaRegion(regionData)) {
    const regionCode = regionData.regionOfChina.id;
    return regionCode != null ? [String(regionCode)] : [];
  }

  return [];
};

// ============ Работа с картой ============

export const clearMapContainer = (containerId = 'mapContainerProjectMap') => {
  const container = document.getElementById(containerId);
  if (!container) return;

  while (container.firstChild) {
    container.removeChild(container.firstChild);
  }
};

export const getProjectsByRegions = (countryData, isRussia) =>
  isRussia ? countryData?.projectsByRegionsRussia : countryData?.projectsByRegionsChina;

export const findRegionData = (projectsByRegions, regionCode, isRussia) => {
  if (!projectsByRegions) return null;

  return projectsByRegions.find((r) => {
    if (isRussia) {
      return r.regionOfRussia?.regionCodes?.includes(regionCode);
    }
    return r.regionOfChina?.id === regionCode;
  });
};

export const getMaxRegionProjects = (projects, countryCode, isRussia) => {
  const countryData = projects[countryCode];
  const projectsByRegions = getProjectsByRegions(countryData, isRussia);

  if (!projectsByRegions?.length) return 0;

  return projectsByRegions.reduce((max, region) => Math.max(max, region.countProjects ?? 0), 0);
};

// ============ Стилизация ============

export const calculateColor = (projectsCount, maxProjects) => {
  if (projectsCount <= 0) return COLOR_DEFAULT;
  if (projectsCount >= maxProjects) return COLOR_MAX;

  const colors = COLORS_LEGEND;
  const colorIndex = Math.round((projectsCount / maxProjects) * (colors.length - 1));
  return colors[Math.min(colorIndex, colors.length - 1)] ?? COLOR_DEFAULT;
};

export const createStyle = (fillColor, options = {}) => ({
  color: COLOR_BORDER,
  fillColor,
  weight: 1,
  opacity: options.opacity ?? 1,
});

// ============ Специальные территории ============

export const isSpecialTerritoryRegion = (regionCode, selectedCountryId, currentRegionType, countries) => {
  if (currentRegionType !== COUNTRY_NAME.CHINA) return false;

  const chinaCountry = countries.find((c) => c.code === CHINA_CODE);
  if (!chinaCountry || selectedCountryId !== chinaCountry.id) return false;

  return Object.hasOwn(REGION_TO_COUNTRY_MAPPING, regionCode);
};

export const getSpecialTerritoryData = (regionCode, selectedCountryId, currentRegionType, countries, projects) => {
  if (currentRegionType !== COUNTRY_NAME.CHINA) return null;

  const selectedCountry = countries.find((c) => c.id === selectedCountryId);
  if (!selectedCountry || !isSpecialTerritoryCode(selectedCountry.code)) return null;

  const expectedRegionCode = SPECIAL_TERRITORY_MAPPING[selectedCountry.code];
  if (regionCode !== expectedRegionCode) return null;

  const countryData = projects[selectedCountry.code];
  if (!countryData) return null;

  return {
    countProjects: countryData.mpkProjects?.length ?? 0,
    countProjectsHasMpk: countryData.countProjectsHasMpk ?? 0,
    investmentsSum: countryData.investmentsSum,
    mpkProjects: countryData.mpkProjects ?? [],
  };
};

// ============ Стилизация карты мира ============

export const styleWorldMap = (feature, projects, maxProjects) => {
  const code = Number(feature.properties.iso_n3).toString();

  // Гренландия - белая
  if (code == GREENLAND_CODE) {
    return createStyle('white', { opacity: 0.5 });
  }

  const projectsCount = projects[code]?.mpkProjects?.length ?? 0;
  const color = calculateColor(projectsCount, maxProjects);

  return createStyle(color);
};

// ============ Стилизация региональной карты ============

export const styleRegionMap = (
  feature,
  projects,
  countries,
  maxRegionProjects,
  currentRegionType,
  selectedCountryId,
) => {
  const regionCode = feature.properties.code;

  // проверяем - специальная территория на карте Китая (белая)
  if (isSpecialTerritoryRegion(regionCode, selectedCountryId, currentRegionType, countries)) {
    return createStyle('white', { opacity: 0.5 });
  }

  // Если просматриваем карту специальной территории (Тайвань, Гонконг, Макао)
  if (currentRegionType === COUNTRY_NAME.CHINA) {
    const selectedCountry = countries.find((c) => c.id === selectedCountryId);
    if (selectedCountry && isSpecialTerritoryCode(selectedCountry.code)) {
      const expectedRegionCode = SPECIAL_TERRITORY_MAPPING[selectedCountry.code];
      if (regionCode === expectedRegionCode) {
        const countryData = projects[selectedCountry.code];
        const projectsCount = countryData?.mpkProjects?.length ?? 0;
        return createStyle(calculateColor(projectsCount, maxRegionProjects));
      }
      return createStyle(COLOR_DEFAULT);
    }
  }

  // Обычные регионы
  const isRussia = currentRegionType === COUNTRY_NAME.RUSSIA;
  const countryCode = isRussia ? RUSSIA_CODE : CHINA_CODE;
  const countryData = projects[countryCode];

  if (!countryData) return createStyle(COLOR_DEFAULT);

  const projectsByRegions = getProjectsByRegions(countryData, isRussia);
  if (!projectsByRegions) return createStyle(COLOR_DEFAULT);

  const regionData = findRegionData(projectsByRegions, regionCode, isRussia);
  const projectsCount = regionData?.countProjects ?? 0;
  const color = calculateColor(projectsCount, maxRegionProjects);

  return createStyle(color);
};
